// @ts-strict-ignore
import { useState } from 'react';
import { tss } from 'tss-react/mui';
import { Button, TextField } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import extractEmailAddressesAndPhoneNumbers from '@/services/extractEmailAddressesAndPhoneNumbers';
import { FileReaderInputField } from '../FileReaderInputField';
import { commitAddRecipientsToListMutation } from './AddRecipientsToListMutation';
import {
    emailAddressesFromRecipients,
    phoneNumbersFromRecipients,
} from './getFromRecipients';
import type { RecipientListInput_recipientList$key } from './__generated__/RecipientListInput_recipientList.graphql.ts';

const useStyles = tss.create({
    respondentList_addToListButtons: {
        marginTop: 24,
        textAlign: 'right',
        display: 'flex',
        flexBasis: '50%',
    },
});

export type RecipientListInputProps = {
    recipientList: RecipientListInput_recipientList$key;
    autoFocusIfEmpty?: boolean;
};

export function RecipientListInput(props: RecipientListInputProps) {
    const { autoFocusIfEmpty = false } = props;
    const recipientList = useFragment(
        fragments.recipientList,
        props.recipientList,
    );
    const { recipients, id: recipientListId } = recipientList;

    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const { classes } = useStyles();

    const onClickAddToList = async () => {
        const respondentEmailAddresses = emailAddressesFromRecipients(
            recipients,
        ).map((recipient) => recipient.label);
        const respondentPhoneNumbers = phoneNumbersFromRecipients(
            recipients,
        ).map((recipient) => recipient.label);

        const { emailAddresses, phoneNumbers } =
            extractEmailAddressesAndPhoneNumbers(
                text,
                respondentEmailAddresses,
                respondentPhoneNumbers,
            );

        if (emailAddresses.length === 0 && phoneNumbers.length === 0) {
            return;
        }

        // Cleanup respondent input (removes added emails and phone-numbers)

        let nextText = text;
        emailAddresses.forEach((email) => {
            nextText = nextText.replace(
                new RegExp(escapeRegExp(email), 'ig'),
                '',
            );
        });

        phoneNumbers.forEach((phone) => {
            nextText = nextText.replace(phone, '');
        });
        nextText = nextText.trim();

        try {
            setSaving(true);
            await commitAddRecipientsToListMutation({
                recipientListId,
                emailAddresses,
                phoneNumbers,
            });
        } catch (e) {
            console.error(e);
            // what to do...
        } finally {
            //clear the value text after its done
            setText(nextText ? nextText : '');
            setSaving(false);
        }
    };

    const handleUpload = (files: File[]) => {
        files[0].text().then(setText);
    };
    return (
        <div>
            <FormattedMessage defaultMessage="Email address or phone number">
                {(chunks) => (
                    <TextField
                        value={text}
                        fullWidth
                        autoFocus={autoFocusIfEmpty && recipients.length === 0}
                        multiline
                        disabled={saving}
                        rows="1"
                        maxRows="10"
                        label={chunks[0]}
                        onChange={(event) => setText(event.target.value)}
                    />
                )}
            </FormattedMessage>
            <div className={classes.respondentList_addToListButtons}>
                <FileReaderInputField onUpload={handleUpload}>
                    <Button color="secondary" type="button">
                        <FormattedMessage defaultMessage="Import file" />
                    </Button>
                </FileReaderInputField>
                <Button onClick={onClickAddToList} disabled={!text}>
                    <FormattedMessage defaultMessage="Add to List" />
                </Button>
            </div>
        </div>
    );
}

function escapeRegExp(s: string) {
    // $& means the whole matched string
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const fragments = {
    recipientList: graphql`
        fragment RecipientListInput_recipientList on RecipientList {
            id
            recipients {
                type
                emailAddress
                phoneNumber
                respondentId
                preRegisteredMetaQuestionResponses {
                    questionId: originalQuestionId
                    selectedResponseOptions {
                        value
                    }
                }
            }
        }
    `,
};

export default RecipientListInput;
